import { useEffect } from "react";
import { ArrowLeft, Crown, Calendar, Phone, MapPin, FileText, CheckCircle2, XCircle, AlertTriangle, Clock } from "lucide-react";

const PRECIOS_REGALOS = [
  { tipo: "Sin regalo",                       valor: "$0" },
  { tipo: "Detalle de bienvenida básico",     valor: "$20" },
  { tipo: "Tarjeta de regalo de tienda",      valor: "$50" },
  { tipo: "Cena para dos personas",           valor: "$80" },
  { tipo: "Noche de hotel adicional",         valor: "$120" },
  { tipo: "Paquete especial premium",         valor: "$150" },
];

const TABLA_PAGOS = [
  { fam: 1,  pago: "$0",     nota: "Comisión de la empresa" },
  { fam: 2,  pago: "$300",   nota: "$300 al agente" },
  { fam: 3,  pago: "$600",   nota: "$300 × 2 al agente" },
  { fam: 4,  pago: "$900",   nota: "$300 × 3 al agente" },
  { fam: 5,  pago: "$1,200", nota: "$300 × 4 al agente" },
  { fam: 6,  pago: "$1,500", nota: "$300 × 5 al agente" },
  { fam: 10, pago: "$2,700", nota: "$300 × 9 al agente", highlight: true },
];

const Section = ({ id, num, title, children, accent = "amber" }) => (
  <section id={id} className="scroll-mt-24 mb-10">
    <div className="flex items-baseline gap-3 mb-4">
      <span className={`text-${accent}-400 font-mono font-black text-sm`}>{num}.</span>
      <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight">{title}</h2>
    </div>
    <div className="text-slate-300 text-sm sm:text-base leading-relaxed space-y-3 pl-7">{children}</div>
  </section>
);

const OpcTerminosPage = () => {
  useEffect(() => {
    document.title = "Términos y Condiciones OPC/DPC · ClubViajemos";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a14] via-[#0d0d1c] to-[#0a0a14] text-white" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="opc-terminos-page">
      {/* TOP STRIP */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-slate-900 text-center py-2 text-xs sm:text-sm font-black tracking-wider sticky top-0 z-40">
        <FileText className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        TÉRMINOS Y CONDICIONES · MÓDULO OPC / DPC · CLUBVIAJEMOS
      </div>

      {/* HEADER */}
      <header className="px-4 pt-12 pb-8 max-w-4xl mx-auto">
        <a href="/opc" className="inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Volver al Proyecto OPC
        </a>
        <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-3">Documento oficial</p>
        <h1 className="text-3xl sm:text-5xl font-black leading-tight mb-3">
          Términos y Condiciones<br />
          <span className="text-amber-300">Módulo OPC / DPC</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl">
          Outside Public Contact / Digital Public Contact — Tour Only — Familias Calificadas
        </p>
        <div className="flex flex-wrap gap-4 mt-5 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Vigencia: desde 3 de junio de 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Weston, Florida · USA</span>
        </div>
      </header>

      {/* INDEX */}
      <nav className="px-4 max-w-4xl mx-auto mb-10">
        <div className="rounded-2xl border border-amber-400/20 bg-slate-900/40 p-5">
          <p className="text-[10px] font-mono uppercase tracking-[2px] text-amber-400 mb-3">Índice</p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-300 list-decimal list-inside">
            {[
              ["s1", "Qué es el Módulo OPC / DPC"],
              ["s2", "Qué es una Familia Calificada"],
              ["s3", "Estructura de Pagos"],
              ["s4", "Cierre Semanal y Día de Pago"],
              ["s5", "Sistema de Regalos y Premios"],
              ["s6", "Ejemplos Completos de Liquidación"],
              ["s7", "Resumen de Condiciones"],
              ["s8", "Obligaciones del Agente"],
              ["s9", "Causas por las que NO se Genera Comisión"],
              ["s10", "Proceso de Disputa o Reclamo"],
              ["s11", "Vigencia y Modificaciones"],
              ["s12", "Aceptación del Agente"],
            ].map(([id, txt]) => (
              <li key={id}><a href={`#${id}`} className="hover:text-amber-300">{txt}</a></li>
            ))}
          </ol>
        </div>
      </nav>

      {/* CONTENT */}
      <main className="px-4 max-w-4xl mx-auto pb-12">

        <Section id="s1" num="1" title="Qué es el Módulo OPC / DPC">
          <p>
            El Módulo <strong className="text-amber-300">OPC (Outside Public Contact)</strong> y <strong className="text-amber-300">DPC (Digital Public Contact)</strong> son formas de generar ingresos como agente de ClubViajemos mediante la captación de familias calificadas que visitan el hotel para una presentación de <strong>120 minutos mínimo</strong> bajo la modalidad <em>tour only</em> (sin hospedaje incluido).
          </p>
          <p>
            La presentación está a cargo del equipo profesional de ventas de ClubViajemos. La función del agente OPC/DPC se limita exclusivamente a la captación y agendamiento previo de la familia.
          </p>
        </Section>

        <Section id="s2" num="2" title="Qué es una Familia Calificada">
          <p>
            Para que una visita cuente como familia calificada y genere comisión, deben cumplirse <strong className="text-amber-300">TODOS</strong> los siguientes requisitos:
          </p>
          <ul className="space-y-2 mt-2">
            {[
              "Estar formada por una pareja o al menos dos adultos mayores de edad.",
              "Ambos adultos presentes durante TODA la presentación.",
              "Ambos mayores de 25 años (excepción aprobada por supervisión).",
              "Tener capacidad de decisión sobre las compras del hogar.",
              "Completar el tour completo de mínimo 120 minutos.",
              "No haber asistido a un tour similar en los últimos 12 meses en nuestra empresa.",
            ].map((it, i) => (
              <li key={i} className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" /><span>{it}</span></li>
            ))}
          </ul>
        </Section>

        <Section id="s3" num="3" title="Estructura de Pagos — Cómo funciona">
          <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-4 mb-4">
            <p className="text-amber-200 font-bold leading-snug">
              Regla principal: la primera familia calificada de cada semana corresponde a la empresa.
            </p>
            <p className="text-sm text-slate-300 mt-1">
              El pago al agente empieza a partir de la <strong>SEGUNDA familia calificada</strong> enviada en la semana.
            </p>
          </div>
          <p><strong>Comisión por familia calificada:</strong> $300 USD.</p>
          <p><strong>Fórmula:</strong> <code className="bg-slate-800 px-2 py-0.5 rounded text-amber-300">(Total familias enviadas − 1) × $300</code></p>
          <p><strong>Ejemplo:</strong> 5 familias = (5 − 1) × $300 = <strong className="text-emerald-300">$1,200 USD</strong>.</p>

          {/* TABLA DE PAGOS */}
          <div className="mt-5 rounded-xl overflow-hidden border border-white/10 bg-slate-900/40">
            <div className="grid grid-cols-3 bg-slate-800/60 text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">
              <div className="p-3">Familias enviadas</div>
              <div className="p-3 text-right">Pago bruto al agente</div>
              <div className="p-3 text-right">Detalle</div>
            </div>
            {TABLA_PAGOS.map((row) => (
              <div key={row.fam} className={`grid grid-cols-3 text-sm border-t border-white/5 ${row.highlight ? "bg-amber-400/10" : ""}`}>
                <div className={`p-3 font-bold ${row.highlight ? "text-amber-300" : "text-white"}`}>{row.fam}</div>
                <div className={`p-3 text-right font-black ${row.highlight ? "text-amber-300" : "text-emerald-300"}`}>{row.pago}</div>
                <div className="p-3 text-right text-xs text-slate-400">{row.nota}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="s4" num="4" title="Cierre Semanal y Día de Pago">
          <ul className="space-y-2">
            {[
              { l: "Semana contable:", v: "Lunes 12:00 AM a domingo 11:59 PM (hora Miami)." },
              { l: "Confirmación de familias calificadas:", v: "Lunes siguiente al cierre." },
              { l: "Día de pago:", v: "Todos los viernes de la semana siguiente al cierre." },
              { l: "Contabilidad:", v: "Semanal e independiente — cada semana empieza en cero. Las familias de la semana anterior NO se acumulan." },
            ].map((it, i) => (
              <li key={i} className="flex flex-col sm:flex-row gap-1 sm:gap-2.5">
                <span className="text-amber-300 font-bold sm:min-w-[260px]">{it.l}</span>
                <span>{it.v}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section id="s5" num="5" title="Sistema de Regalos y Premios de Captación">
          <p>El agente puede ofrecer regalos para incentivar la captación. Si el regalo tiene valor económico, dicho valor se descuenta de la comisión del agente. Lista oficial aprobada:</p>
          <div className="mt-3 rounded-xl overflow-hidden border border-white/10 bg-slate-900/40">
            <div className="grid grid-cols-2 bg-slate-800/60 text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">
              <div className="p-3">Tipo de regalo</div>
              <div className="p-3 text-right">Costo</div>
            </div>
            {PRECIOS_REGALOS.map((r, i) => (
              <div key={r.tipo} className="grid grid-cols-2 text-sm border-t border-white/5">
                <div className="p-3 text-white">{r.tipo}</div>
                <div className="p-3 text-right font-mono font-bold text-amber-300">{r.valor}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-rose-300 italic mt-3">El ofrecimiento de regalos fuera de la lista oficial resulta en la pérdida total de la comisión correspondiente.</p>
        </Section>

        <Section id="s6" num="6" title="Ejemplos Completos de Liquidación">
          <div className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <p className="text-amber-300 font-bold mb-1">Ejemplo A — Sin regalos</p>
              <p className="text-sm">5 familias calificadas en la semana, sin regalo:</p>
              <p className="font-mono text-emerald-300 mt-1">(5 − 1) × $300 = <strong>$1,200 USD</strong></p>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <p className="text-amber-300 font-bold mb-1">Ejemplo B — Con regalos</p>
              <p className="text-sm">5 familias calificadas + 4 cenas para dos ($80 c/u):</p>
              <p className="font-mono text-emerald-300 mt-1">(5 − 1) × $300 − (4 × $80) = $1,200 − $320 = <strong>$880 USD</strong></p>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <p className="text-amber-300 font-bold mb-1">Ejemplo C — Volumen alto</p>
              <p className="text-sm">10 familias calificadas + 9 tarjetas de tienda ($50 c/u):</p>
              <p className="font-mono text-emerald-300 mt-1">(10 − 1) × $300 − (9 × $50) = $2,700 − $450 = <strong>$2,250 USD</strong></p>
            </div>
          </div>
        </Section>

        <Section id="s7" num="7" title="Resumen de Condiciones">
          <ul className="space-y-2">
            <li className="flex gap-2.5"><span className="text-amber-400">•</span><span><strong>Modalidad:</strong> Tour Only — 120 minutos mínimo en el hotel.</span></li>
            <li className="flex gap-2.5"><span className="text-amber-400">•</span><span><strong>Cierre:</strong> Lunes 12:00 AM a Domingo 11:59 PM hora Miami.</span></li>
            <li className="flex gap-2.5"><span className="text-amber-400">•</span><span><strong>Comisión:</strong> $300 USD por familia calificada, a partir de la SEGUNDA familia.</span></li>
            <li className="flex gap-2.5"><span className="text-amber-400">•</span><span><strong>Pago:</strong> Viernes siguiente al cierre semanal.</span></li>
            <li className="flex gap-2.5"><span className="text-amber-400">•</span><span><strong>Sin acumulación:</strong> Cada semana arranca desde cero.</span></li>
          </ul>
        </Section>

        <Section id="s8" num="8" title="Obligaciones del Agente">
          <ul className="space-y-2 mt-2">
            {[
              "Registrar cada familia ANTES de la visita en el sistema de citas del hotel.",
              "Informar a la familia de la duración del tour (120 min) antes de su llegada.",
              "No hacer promesas económicas adicionales a las establecidas en este documento.",
              "Usar exclusivamente los regalos de la lista oficial aprobada.",
              "No enviar familias que no cumplan los requisitos de calificación.",
              "Mantener registro personal de las familias enviadas cada semana.",
              "Notificar al supervisor cualquier problema detectado en el registro.",
            ].map((it, i) => (
              <li key={i} className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" /><span>{it}</span></li>
            ))}
          </ul>
        </Section>

        <Section id="s9" num="9" title="Causas por las que NO se Genera Comisión">
          <ul className="space-y-2 mt-2">
            {[
              "La familia no completó los 120 minutos del tour.",
              "Uno o ambos adultos no estuvieron presentes durante todo el tour.",
              "La familia no cumplió los requisitos de calificación al momento de la llegada.",
              "La familia fue enviada por un agente diferente al registrado en el sistema.",
              "La familia asistió a un tour de la empresa en los últimos 12 meses.",
              "La visita no fue registrada previamente en el sistema de citas.",
              "El agente ofreció un regalo fuera de la lista oficial.",
            ].map((it, i) => (
              <li key={i} className="flex gap-2.5"><XCircle className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" /><span>{it}</span></li>
            ))}
          </ul>
        </Section>

        <Section id="s10" num="10" title="Proceso de Disputa o Reclamo">
          <ul className="space-y-2">
            <li className="flex gap-2.5"><Clock className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /><span><strong>Plazo para reclamos:</strong> dentro de los 3 días hábiles siguientes al cierre semanal.</span></li>
            <li className="flex gap-2.5"><Clock className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /><span><strong>Plazo para revisión:</strong> ClubViajemos responderá dentro de 5 días hábiles.</span></li>
            <li className="flex gap-2.5"><Phone className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /><span><strong>Primer contacto:</strong> tu supervisor directo. Si no se resuelve, escalar al área administrativa de ClubViajemos.</span></li>
          </ul>
        </Section>

        <Section id="s11" num="11" title="Vigencia y Modificaciones">
          <p>Estos términos rigen <strong>a partir del 3 de junio de 2026</strong> y hasta nueva comunicación por escrito.</p>
          <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-4 mt-3">
            <p className="text-sm flex gap-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <span>
                ClubViajemos se reserva el derecho de modificar estas condiciones con un <strong>aviso previo de 7 días hábiles</strong>. La continuación de operaciones implica aceptación de los nuevos términos. <strong>Las comisiones ya generadas y no pagadas no se verán afectadas</strong> por modificaciones posteriores.
              </span>
            </p>
          </div>
        </Section>

        <Section id="s12" num="12" title="Aceptación del Agente">
          <p>Al firmar este documento, el agente declara:</p>
          <ul className="space-y-1.5 mt-2 list-disc list-inside pl-2">
            <li>Haber leído y comprendido en su totalidad los términos y condiciones.</li>
            <li>Aceptar el modelo de pago descrito, incluyendo la regla de la primera familia de la semana.</li>
            <li>Comprometerse a cumplir las obligaciones del agente listadas en la cláusula 8.</li>
            <li>Reconocer que ClubViajemos puede modificar estos términos con previo aviso.</li>
          </ul>

          {/* SIGN BLOCK */}
          <div className="mt-6 rounded-2xl border border-amber-400/30 bg-slate-900/60 p-5 sm:p-6">
            <p className="text-[10px] font-mono uppercase tracking-[2px] text-amber-300 mb-4">Datos del agente</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-slate-400 text-xs mb-1">Nombre del agente</p>
                <div className="border-b border-slate-600 pb-1 h-6">&nbsp;</div>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">ID de agente ClubViajemos</p>
                <div className="border-b border-slate-600 pb-1 h-6">&nbsp;</div>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">Fecha de aceptación</p>
                <div className="border-b border-slate-600 pb-1 h-6">&nbsp;</div>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">Firma del agente</p>
                <div className="border-b border-slate-600 pb-1 h-6">&nbsp;</div>
              </div>
              <div className="sm:col-span-2">
                <p className="text-slate-400 text-xs mb-1">Firma de supervisor / ClubViajemos</p>
                <div className="border-b border-slate-600 pb-1 h-6">&nbsp;</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 italic mt-4">
              Documento oficial — ClubViajemos / Travel Empire · Weston, Florida · clubviajemos.com
            </p>
          </div>
        </Section>

        {/* CTA PRINT/VOLVER */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => window.print()}
            data-testid="opc-terminos-print"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform"
          >
            <FileText className="h-4 w-4" /> Imprimir / Guardar PDF
          </button>
          <a
            href="/opc"
            className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al Proyecto OPC
          </a>
        </div>
      </main>

      <footer className="py-10 px-4 text-center text-xs text-slate-500 border-t border-white/5">
        <p>&copy; 2026 Club Viajemos / Travel Empire · Documento confidencial</p>
        <p className="mt-1">Para uso exclusivo de agentes acreditados del Módulo OPC/DPC</p>
      </footer>
    </div>
  );
};

export default OpcTerminosPage;
